import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Github, 
  ExternalLink, 
  Link2, 
  Unlink, 
  Loader2, 
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Settings2,
  Plug
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { orgIntegrationsApi, githubAppApi } from '../../services/api';

// Platform icons mapping
const PLATFORM_ICONS = {
  github: Github,
  jira: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M11.53 2c0 2.4 1.97 4.35 4.35 4.35h1.78v1.7c0 2.4 1.94 4.34 4.34 4.35V2.84a.84.84 0 0 0-.84-.84H11.53zM6.77 6.8a4.36 4.36 0 0 0 4.34 4.34h1.8v1.72a4.36 4.36 0 0 0 4.34 4.34V7.63a.84.84 0 0 0-.83-.83H6.77zM2 11.6c0 2.4 1.95 4.34 4.35 4.34h1.78v1.72c0 2.4 1.94 4.35 4.34 4.35V12.43a.84.84 0 0 0-.84-.84H2z"/>
    </svg>
  ),
  linear: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M3.356 20.644a12.07 12.07 0 0 1-1.037-1.643l9.967-9.967a.5.5 0 0 1 .708 0l1.972 1.972a.5.5 0 0 1 0 .708l-9.967 9.967a12.07 12.07 0 0 1-1.643-1.037zm-.81-3.195a12.003 12.003 0 0 1-.397-1.52l8.26-8.26a.5.5 0 0 1 .707 0l1.215 1.215a.5.5 0 0 1 0 .707l-8.26 8.26a12.003 12.003 0 0 1-1.526-.402zm-.413-3.172a12.07 12.07 0 0 1-.09-1.448c0-.205.005-.41.017-.612l6.266-6.266a.5.5 0 0 1 .708 0l1.457 1.457a.5.5 0 0 1 0 .707l-6.41 6.41a12.154 12.154 0 0 1-1.948-.248zm.698-3.61a12.044 12.044 0 0 1 2.62-4.197A12.044 12.044 0 0 1 9.648 4.05l-4.897 4.897a.5.5 0 0 1-.708 0l-1.212-1.212a12.068 12.068 0 0 1 0-.068zm18.126 9.69l-9.967-9.966a.5.5 0 0 0-.708 0l-1.972 1.972a.5.5 0 0 0 0 .708l9.967 9.966a12.07 12.07 0 0 0 2.68-2.68zm1.276-4.323l-8.26-8.26a.5.5 0 0 0-.707 0l-1.215 1.215a.5.5 0 0 0 0 .707l8.26 8.26a12.07 12.07 0 0 0 1.922-1.922zm.412-3.172a12.07 12.07 0 0 0 .09-1.448c0-.205-.005-.41-.017-.612l-6.266-6.266a.5.5 0 0 0-.708 0l-1.457 1.457a.5.5 0 0 0 0 .707l6.41 6.41c.614.037 1.272.098 1.948.248.053-.165.103-.33.15-.496z"/>
    </svg>
  ),
  gitlab: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="m23.546 10.93-.963-2.636-1.946-5.332a.401.401 0 0 0-.762 0l-1.945 5.332H6.07L4.124 2.962a.401.401 0 0 0-.762 0L1.417 8.294.454 10.93a.791.791 0 0 0 .287.891l11.259 8.18 11.259-8.18a.791.791 0 0 0 .287-.891"/>
    </svg>
  ),
  azure_devops: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
      <path d="M0 8.899l2.247-2.966 8.405-3.416V.045l7.37 5.393L2.966 8.36v8.224L0 15.73zm24-4.54v14.652L18.247 24l-9.303-3.382v3.349l-5.888-7.933 15.057-1.27V4.359z"/>
    </svg>
  )
};

const OrgIntegrationsSection = ({ colors, canEdit, enabledIntegrations }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [integrations, setIntegrations] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlatform, setExpandedPlatform] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null);
  const [githubSetup, setGithubSetup] = useState(null);
  const [connecting, setConnecting] = useState(null);

  // Load integrations and platforms
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [integrationsRes, platformsRes, githubSetupRes] = await Promise.all([
        orgIntegrationsApi.getIntegrations(),
        orgIntegrationsApi.getAvailablePlatforms(),
        githubAppApi.getSetupInfo().catch(() => ({ data: {} }))
      ]);
      setIntegrations(integrationsRes.data);
      setPlatforms(platformsRes.data.platforms || []);
      setGithubSetup(githubSetupRes.data);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle GitHub installation callback
  useEffect(() => {
    const githubInstalled = searchParams.get('github_installed');
    const githubError = searchParams.get('github_error');
    const installationId = searchParams.get('installation_id');
    
    if (githubError === 'true') {
      toast.error('GitHub App installation failed. Please try again.');
      
      // Clear the URL params
      searchParams.delete('github_error');
      setSearchParams(searchParams, { replace: true });
      
      // Expand GitHub section
      setExpandedPlatform('github');
    } else if (githubInstalled === 'true') {
      toast.success('GitHub App installed successfully! Linking to your organization...');
      
      // Clear the URL params
      searchParams.delete('github_installed');
      searchParams.delete('installation_id');
      setSearchParams(searchParams, { replace: true });
      
      // If we have an installation ID, link it
      if (installationId) {
        linkGitHubInstallation(installationId);
      } else {
        // Just refresh the data
        loadData();
      }
      
      // Expand GitHub section to show the result
      setExpandedPlatform('github');
    } else if (installationId && !githubInstalled) {
      // Handle case where we have installation_id but no github_installed flag
      // This can happen on update/request actions
      searchParams.delete('installation_id');
      setSearchParams(searchParams, { replace: true });
      loadData();
      setExpandedPlatform('github');
    }
  }, [searchParams, setSearchParams, loadData]);

  const linkGitHubInstallation = async (installationId) => {
    try {
      setConnecting('github');
      await githubAppApi.linkInstallation(installationId);
      toast.success('GitHub connected to your organization!');
      loadData();
    } catch (error) {
      console.error('Failed to link GitHub installation:', error);
      toast.error('Failed to link GitHub installation. Please try again from organization settings.');
    } finally {
      setConnecting(null);
    }
  };

  const handleSyncGitHubRepos = async () => {
    try {
      setConnecting('sync_repos');
      const response = await githubAppApi.syncRepos();
      toast.success(`Synced ${response.data.count || 0} repositories`);
      loadData();
    } catch (error) {
      console.error('Failed to sync GitHub repos:', error);
      toast.error('Failed to sync repositories');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platformId) => {
    try {
      setDisconnecting(platformId);
      
      if (platformId === 'github') {
        await orgIntegrationsApi.disconnectGitHub();
      } else if (platformId === 'jira') {
        await orgIntegrationsApi.disconnectJira();
      } else if (platformId === 'linear') {
        await orgIntegrationsApi.disconnectLinear();
      }
      
      toast.success(`${platformId.charAt(0).toUpperCase() + platformId.slice(1)} disconnected`);
      loadData();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      toast.error('Failed to disconnect integration');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnect = async (platformId) => {
    if (platformId === 'github') {
      // Check if GitHub App is configured
      if (!githubSetup?.installation_url) {
        toast.error('GitHub App is not configured. Please contact your administrator.');
        return;
      }
      
      // Redirect to GitHub App installation page
      // GitHub will redirect back to our callback URL after installation
      window.location.href = githubSetup.installation_url;
    } else if (platformId === 'azure_devops') {
      toast.info('Azure DevOps uses personal accounts. Connect from your Profile tab → Connected Accounts.');
    } else {
      toast.info(`${platformId} integration coming soon`);
    }
  };

  const getConnectionStatus = (platformId) => {
    if (!integrations) return null;
    return integrations[platformId];
  };

  const toggleExpanded = (platformId) => {
    setExpandedPlatform(expandedPlatform === platformId ? null : platformId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading integrations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Plug className={cn("w-5 h-5", colors.textAccent)} />
        <div>
          <h3 className={cn("text-lg font-semibold", colors.textPrimary)}>
            Integrations
          </h3>
          <p className={cn("text-sm", colors.textMuted)}>
            Connect platforms for your organization. Products can then choose which to use.
          </p>
        </div>
      </div>

      {/* Source Control & Ticketing Section */}
      <Card className={cn("border", colors.bgCard, colors.borderPrimary)}>
        <CardContent className="p-0">
          <div className={cn("px-4 py-3 border-b", colors.borderPrimary, colors.bgTertiary)}>
            <h4 className={cn("font-medium text-sm", colors.textPrimary)}>
              Source Control & Ticketing
            </h4>
          </div>
          
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {platforms
              .filter((platform) => !enabledIntegrations || enabledIntegrations[platform.id] !== false)
              .map((platform) => {
              const connection = getConnectionStatus(platform.id);
              const isConnected = !!connection;
              const isExpanded = expandedPlatform === platform.id;
              const isComingSoon = platform.status === 'coming_soon';
              const IconComponent = PLATFORM_ICONS[platform.id] || Settings2;
              
              return (
                <div key={platform.id} className="group">
                  {/* Platform Header */}
                  <button
                    onClick={() => !isComingSoon && toggleExpanded(platform.id)}
                    disabled={isComingSoon}
                    className={cn(
                      "w-full px-4 py-4 flex items-center justify-between text-left transition-colors",
                      !isComingSoon && "hover:bg-gray-50 dark:hover:bg-gray-800/50",
                      isComingSoon && "opacity-60 cursor-not-allowed"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        isConnected 
                          ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" 
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      )}>
                        {typeof IconComponent === 'function' ? <IconComponent /> : <IconComponent className="w-5 h-5" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={cn("font-medium", colors.textPrimary)}>
                            {platform.name}
                          </span>
                          {isConnected && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Connected
                            </Badge>
                          )}
                          {isComingSoon && (
                            <Badge variant="secondary" className="text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              Coming Soon
                            </Badge>
                          )}
                        </div>
                        <p className={cn("text-sm", colors.textMuted)}>
                          {isConnected 
                            ? `${connection.org_name || 'Organization'} • ${connection.available_repos?.length || 0} repos accessible`
                            : platform.description
                          }
                        </p>
                      </div>
                    </div>
                    
                    {!isComingSoon && (
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className={cn("w-5 h-5", colors.textMuted)} />
                        ) : (
                          <ChevronRight className={cn("w-5 h-5", colors.textMuted)} />
                        )}
                      </div>
                    )}
                  </button>
                  
                  {/* Expanded Content */}
                  {isExpanded && !isComingSoon && (
                    <div className={cn("px-4 pb-4 pt-0 ml-13", colors.bgSecondary)}>
                      <div className={cn("p-4 rounded-lg border", colors.borderPrimary, colors.bgCard)}>
                        {isConnected ? (
                          <div className="space-y-4">
                            {/* Connection Info */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <span className={cn("text-sm", colors.textMuted)}>Organization</span>
                                <span className={cn("text-sm font-medium", colors.textPrimary)}>
                                  {connection.org_name}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={cn("text-sm", colors.textMuted)}>Connected</span>
                                <span className={cn("text-sm", colors.textPrimary)}>
                                  {new Date(connection.installed_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={cn("text-sm", colors.textMuted)}>Repositories</span>
                                <span className={cn("text-sm font-medium", colors.textPrimary)}>
                                  {connection.available_repos?.length || 0} accessible
                                </span>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                              {platform.id === 'github' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleSyncGitHubRepos}
                                  disabled={connecting === 'sync_repos' || !canEdit}
                                >
                                  {connecting === 'sync_repos' ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                  )}
                                  Sync Repos
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`https://github.com/settings/installations`, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4 mr-2" />
                                Manage Installation
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDisconnect(platform.id)}
                                disabled={disconnecting === platform.id || !canEdit}
                              >
                                {disconnecting === platform.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Unlink className="w-4 h-4 mr-2" />
                                )}
                                Disconnect
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <p className={cn("text-sm", colors.textMuted)}>
                              {platform.id === 'github' 
                                ? 'Install the StackRanked GitHub App to enable commit tracking and issue sync for your products.'
                                : `Connect your ${platform.name} account to enable integration.`
                              }
                            </p>
                            
                            {/* Show configuration status for GitHub */}
                            {platform.id === 'github' && !githubSetup?.is_configured && (
                              <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-md border border-amber-200 dark:border-amber-800">
                                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                                <span className="text-sm text-amber-700 dark:text-amber-300">
                                  GitHub App not configured. Contact your system administrator.
                                </span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-3">
                              <Button
                                onClick={() => handleConnect(platform.id)}
                                disabled={!canEdit || connecting === platform.id || (platform.id === 'github' && !githubSetup?.is_configured)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                              >
                                {connecting === platform.id ? (
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Link2 className="w-4 h-4 mr-2" />
                                )}
                                {platform.id === 'github' ? 'Install GitHub App' : `Connect ${platform.name}`}
                              </Button>
                              
                              {platform.id === 'github' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.open('https://docs.github.com/en/apps/using-github-apps/installing-a-github-app', '_blank')}
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Learn More
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Info Note */}
      <div className={cn("p-4 rounded-lg border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800")}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Connected platforms become available for individual products to configure.
              Each product can choose which repositories or projects to use.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgIntegrationsSection;
